import { z } from 'zod';
import type { LiveEvent, LiveSource } from '@zagru/design-system';
import { apiClient, ZagruProblem, type ApiClient } from '@zagru/platform';

/*
 * The live change stream (G-003) as a `LiveSource` for `LiveUpdatesProvider`.
 *
 * `GET /api/v1/platform/live` is Server-Sent Events. It is read with `fetch` streaming through the API client
 * (`client.sse`), not with `EventSource`: the platform authenticates with a bearer header, which `EventSource`
 * cannot send, and a token in the URL would end up in access logs.
 *
 * Frames the server sends (`LiveController`):
 * - `ready` once: `{ entityTypes, heartbeatSeconds, reconnectAfterSeconds }` — the entity types this viewer receives
 *   (their read permission is granted; everything else is dropped on the server, never filtered here).
 * - `change`: {@link LiveChange}, with the event id as the SSE id.
 * - comment heartbeats, which the parser ignores.
 *
 * Topics are the server's entity types (`LiveEntityCatalog`): `outbound.order`, `outbound.wave`, `inbound.receipt`,
 * `inventory.movement`, `task.task`, `shipment.shipment`, `integration.message`, … — plus {@link LIVE_ALL_TOPIC} for
 * every change. A frame carries no business content: a screen refetches through its own read, which applies every
 * rule the stream cannot.
 *
 * The connection opens on the first subscription, reconnects with exponential backoff (1 s → 60 s, with jitter)
 * after a failure and promptly after the server closes the stream (it does every 30 minutes, to re-evaluate the
 * viewer's permissions), and closes a moment after the last subscriber leaves.
 */

/** `LiveChange` (`one.zagru.platform.events.api.LiveChange`). */
export const liveChangeSchema = z.object({
  entityType: z.string(),
  entityId: z.string(),
  change: z.string(),
  occurredAt: z.string(),
  eventId: z.string(),
});
export type LiveChange = z.infer<typeof liveChangeSchema>;

/** The `ready` frame. */
export const liveReadySchema = z.object({
  entityTypes: z.array(z.string()),
  heartbeatSeconds: z.number(),
  reconnectAfterSeconds: z.number(),
});
export type LiveReady = z.infer<typeof liveReadySchema>;

/** Subscribes to every change the viewer may see. */
export const LIVE_ALL_TOPIC = '*';

export interface LiveSourceOptions {
  /** Defaults to the app's authenticated client. */
  client?: ApiClient;
  minBackoffMs?: number;
  maxBackoffMs?: number;
  /** How long the connection stays open after the last subscriber leaves (absorbs remounts). Default 2 s. */
  lingerMs?: number;
}

export interface AppLiveSource extends LiveSource {
  /** True while a stream is open. */
  readonly connected: boolean;
  /** The entity types the server said this viewer receives (empty until the first `ready`). */
  readonly entityTypes: readonly string[];
  /** Closes the stream and drops every subscriber (sign-out). */
  close(): void;
}

type Callback = (event: LiveEvent) => void;

const SEEN_CAPACITY = 2000;

export function createLiveSource(options: LiveSourceOptions = {}): AppLiveSource {
  const client = options.client ?? apiClient;
  const minBackoff = options.minBackoffMs ?? 1_000;
  const maxBackoff = options.maxBackoffMs ?? 60_000;
  const linger = options.lingerMs ?? 2_000;
  const topics = new Map<string, Set<Callback>>();
  const seen = new Set<string>();
  let running = false;
  let connected = false;
  let entityTypes: readonly string[] = [];
  let controller: AbortController | null = null;
  let wake: (() => void) | null = null;
  let stopTimer: ReturnType<typeof setTimeout> | undefined;
  /** Bumped on every start, so a loop still unwinding from an abort never runs beside its successor. */
  let generation = 0;

  const subscriberCount = () => {
    let n = 0;
    topics.forEach((set) => (n += set.size));
    return n;
  };

  const deliver = (change: LiveChange) => {
    // The server re-reads a short window per node and a reconnect may land on another node: drop repeats.
    if (seen.has(change.eventId)) return;
    seen.add(change.eventId);
    if (seen.size > SEEN_CAPACITY) {
      const oldest = seen.values().next().value;
      if (oldest !== undefined) seen.delete(oldest);
    }
    const at = Date.parse(change.occurredAt);
    const event: LiveEvent<LiveChange> = { topic: change.entityType, payload: change, at: Number.isNaN(at) ? Date.now() : at };
    topics.get(change.entityType)?.forEach((cb) => cb(event));
    const all: LiveEvent<LiveChange> = { ...event, topic: LIVE_ALL_TOPIC };
    topics.get(LIVE_ALL_TOPIC)?.forEach((cb) => cb(all));
  };

  const onFrame = (frame: { event: string; data: string }) => {
    let json: unknown;
    try {
      json = JSON.parse(frame.data);
    } catch {
      return;
    }
    if (frame.event === 'change') {
      const parsed = liveChangeSchema.safeParse(json);
      if (parsed.success) deliver(parsed.data);
    } else if (frame.event === 'ready') {
      const parsed = liveReadySchema.safeParse(json);
      if (parsed.success) entityTypes = parsed.data.entityTypes;
    }
  };

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        wake = null;
        resolve();
      }, ms);
      wake = () => {
        clearTimeout(timer);
        wake = null;
        resolve();
      };
    });

  const backoff = (attempt: number) => {
    const base = Math.min(maxBackoff, minBackoff * 2 ** Math.max(0, attempt - 1));
    return Math.round(base / 2 + Math.random() * (base / 2));
  };

  const loop = async (mine: number) => {
    let attempt = 0;
    const current = () => running && generation === mine;
    while (current()) {
      const own = new AbortController();
      controller = own;
      let delay: number;
      try {
        await client.sse('/api/v1/platform/live', {
          signal: own.signal,
          onOpen: () => {
            connected = true;
            attempt = 0;
          },
          onEvent: onFrame,
        });
        // The server ended the stream (its timeout): reconnect soon, which also refreshes the permissions it applies.
        delay = minBackoff;
      } catch (err) {
        attempt += 1;
        // Capacity or a disabled stream (503) and a missing session (401/403) will not heal in a second.
        const slow = err instanceof ZagruProblem && (err.status === 503 || err.status === 401 || err.status === 403);
        delay = slow ? maxBackoff : backoff(attempt);
      } finally {
        if (controller === own) {
          connected = false;
          controller = null;
        }
      }
      if (!current()) break;
      await sleep(delay);
    }
  };

  const start = () => {
    if (stopTimer) {
      clearTimeout(stopTimer);
      stopTimer = undefined;
    }
    if (running) return;
    running = true;
    generation += 1;
    void loop(generation);
  };

  const stop = () => {
    running = false;
    controller?.abort();
    wake?.();
    entityTypes = [];
  };

  return {
    get connected() {
      return connected;
    },
    get entityTypes() {
      return entityTypes;
    },
    subscribe(topic, callback) {
      let set = topics.get(topic);
      if (!set) {
        set = new Set();
        topics.set(topic, set);
      }
      set.add(callback);
      start();
      return () => {
        const current = topics.get(topic);
        current?.delete(callback);
        if (current?.size === 0) topics.delete(topic);
        if (subscriberCount() === 0 && !stopTimer) {
          stopTimer = setTimeout(() => {
            stopTimer = undefined;
            if (subscriberCount() === 0) stop();
          }, linger);
        }
      };
    },
    close() {
      topics.clear();
      if (stopTimer) clearTimeout(stopTimer);
      stopTimer = undefined;
      stop();
    },
  };
}

/** The app's live source: pass it to `LiveUpdatesProvider` (`source={liveSource}`). */
export const liveSource: AppLiveSource = createLiveSource();
